import React, { ComponentProps, useState, useEffect, useContext } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
// Components
import { Form } from 'react-bootstrap'
import { Col } from 'react-bootstrap'
import FormItem from 'components/utils/FormItem'
import FormDropdown from 'components/utils/FormDropdown'
import LinkToButton from 'components/utils/Button/LinkToButton'
import Header from 'components/utils/Form/Header'
import Footer from 'components/utils/Form/Footer'
// Styles
import { ItemWrapper, OffsetedContainer } from 'styles/common'
// Additional
import { MatchMapVetoRecord } from 'data/MatchMapVetoRecord'
import { AppContext } from 'App'
import { MatchMapVetoContext } from 'components/MatchMapVetos/__MatchMapVetos.context'
import { MatchContext } from 'components/Matches/__Matches.context'
import { FormContext } from 'components/utils/Form.context'
import { Item } from 'components/utils/Dropdown'
import { Form as FormHelper } from 'helpers/form'
import { Data as DataHelper } from 'helpers/data'
import { Routing as RoutingHelper } from 'helpers/routing'

const SubmitKeys = [
  'match_id',
  'action_ident',
  'game_map_id',
  'team_id',
] as Array<keyof MatchMapVetoRecord>

type ValidationKeys = Record<keyof Pick<MatchMapVetoRecord, 'game_map_id'>, any>

const ACTION_IDENT_DATA = [
  { id: 'pick', text: 'Pick' },
  { id: 'ban', text: 'Ban' },
  { id: 'decider_left', text: 'Decider Left' },
]

const MatchMapVetoForm: React.FC<FormHelper.Props<MatchMapVetoRecord>> = (
  p
) => {
  const { db, alert, history } = useContext(AppContext)
  const { params: mmv_params } = useContext(MatchMapVetoContext)
  const { params: m_params } = useContext(MatchContext)

  const [form, setForm] = useState<{
    data?: MatchMapVetoRecord
    state: FormHelper.FormState
  }>({ data: p.data, state: p.state ?? 'new' })

  const opt = {
    alert: {
      manager: alert,
      texts: {
        form: 'Match Map Veto',
      },
    },
    history: history,
    lock: {
      callback: p.locked?.callback,
    },
  }

  const formik = useFormik({
    initialValues: {
      ...form.data,

      match_id: form.data?.match?.id ?? mmv_params?.match_id,

      game_map_id: form.data?.gameMap?.id,
      game_map_text: form.data?.gameMap?.name,

      team_id: form.data?.team?.id,
      team_text: form.data?.team?.full_name,
    },

    onSubmit: async (values) => {
      if (!db?.matchMapVetos) return

      try {
        const data = await FormHelper.submitChanges<MatchMapVetoRecord>({
          action: form.state === 'new' ? 'create' : 'update',
          keys: SubmitKeys,
          data: Object.assign(new MatchMapVetoRecord(), values ?? {}),
          repository: db.matchMapVetos,
          optional: opt,
        })

        if (data?.id) {
          setForm({ data: data, state: 'existing' })

          history?.replace(
            RoutingHelper.getMatchMapVetoDetailUrl({
              tournament:
                m_params?.tournament_id ?? RoutingHelper.NewRecordSymbol,
              match: m_params?.tournament_id ?? RoutingHelper.NewRecordSymbol,
              matchMapVeto: data.id,
            })
          )
        }

        data && p.submitCallback?.(data)
      } catch (err) {
        console.error(err)
      }
    },

    validationSchema: Yup.object<ValidationKeys>({
      game_map_id: Yup.string().required('Game Map is a required field'),
    }),
  })

  const delete_ = async () => {
    if (!db?.matchMapVetos) return

    await FormHelper.delete_({
      data: Object.assign(new MatchMapVetoRecord(), formik.values ?? {}),
      repository: db.matchMapVetos,
      optional: opt,
    })
  }

  const touch = async () => {
    if (!db?.matchMapVetos) return

    await FormHelper.touch({
      data: Object.assign(new MatchMapVetoRecord(), formik.values ?? {}),
      repository: db.matchMapVetos,
      optional: opt,
    })
  }

  const [actionIdent, setActionIdent] = useState<{
    value?: Item
    data?: Array<Item>
    loaded: boolean
  }>({
    value: ACTION_IDENT_DATA.find((a) => a.id === form.data?.action_ident),
    data: ACTION_IDENT_DATA,
    loaded: false,
  })

  const [gameMap, setGameMap] = useState<{
    value?: Item
    data?: Array<Item>
    loaded: boolean
  }>({
    value: {
      id: formik.values.game_map_id ?? '',
      text: formik.values.game_map_text,
    },
    loaded: false,
  })

  const [team, setTeam] = useState<{
    value?: Item
    data?: Array<Item>
    loaded: boolean
  }>({
    value: {
      id: formik.values.team_id ?? '',
      text: formik.values.team_text,
    },
    loaded: false,
  })

  const _setGameMaps = async () => {
    const _data = await DataHelper.getAllGameMaps(db!)

    setGameMap((prev) => ({
      loaded: true,
      value: prev?.value,
      data: _data?.map((s) => {
        return { id: s.id ?? '', text: s.name ?? '' } as Item
      }),
    }))
  }

  const _setTeams = async () => {
    const _data = await db?.matches.get(mmv_params?.match_id)

    setTeam((prev) => ({
      loaded: true,
      value: prev?.value,
      data: [
        { id: _data?.team1_id ?? -1, text: _data?.team1_name },
        { id: _data?.team2_id ?? -1, text: _data?.team2_name },
      ],
    }))
  }

  const handleActionIdentSelect = (item: Item) => {
    formik.setFieldValue('action_ident' as keyof MatchMapVetoRecord, item.id)

    setActionIdent((prev) => ({ ...prev, value: item, data: prev.data ?? [] }))
  }

  const handleGameMapSelect = (item: Item) => {
    formik.setFieldValue('game_map_id' as keyof MatchMapVetoRecord, item.id)

    setGameMap((prev) => ({ ...prev, value: item, data: prev.data ?? [] }))
  }

  const handleTeamSelect = (item: Item) => {
    formik.setFieldValue('team_id' as keyof MatchMapVetoRecord, item.id)

    setTeam((prev) => ({ ...prev, value: item, data: prev.data ?? [] }))
  }

  useEffect(() => {
    _setGameMaps()
    _setTeams()
  }, [])

  //#region Form Components
  const _Match = (
    <Form.Control
      name={'match_id' as keyof MatchMapVetoRecord}
      value={formik.values.match_id}
      hidden
    />
  )

  const _ActionIdent = (
    <FormItem
      label={<Form.Label>Action Ident</Form.Label>}
      input={
        <FormDropdown
          name={'action_ident' as keyof MatchMapVetoRecord}
          items={actionIdent.data ?? []}
          onSelect={handleActionIdentSelect}
          value={actionIdent.value}
          loading={!actionIdent.loaded}
        />
      }
    />
  )

  const _GameMap = (
    <FormItem
      label={<Form.Label>Game Map</Form.Label>}
      input={
        <>
          <FormDropdown
            name={'game_map_id' as keyof MatchMapVetoRecord}
            items={gameMap.data ?? []}
            onSelect={handleGameMapSelect}
            value={gameMap.value}
            required
            isInvalid={formik.submitCount > 0 && !!formik.errors.game_map_id}
            loading={!gameMap.loaded}
          />
          <Form.Control.Feedback type={'invalid'}>
            {formik.errors.game_map_id}
          </Form.Control.Feedback>
        </>
      }
    />
  )

  const _Team = (
    <FormItem
      label={<Form.Label>Team</Form.Label>}
      input={
        <FormDropdown
          name={'team_id' as keyof MatchMapVetoRecord}
          items={team.data ?? []}
          onSelect={handleTeamSelect}
          value={team.value}
          loading={!team.loaded}
          append={
            <LinkToButton
              path={
                formik.values.team_id &&
                RoutingHelper.getTeamDetailUrl({
                  sport: RoutingHelper.NewRecordSymbol,
                  team: formik.values.team_id,
                })
              }
            />
          }
        />
      }
    />
  )
  //#endregion

  const _ComputeHeaderProps = (): ComponentProps<typeof Header> => {
    if (form.state === 'new') return {}

    return {
      useTouch: true,
      locked: p.locked?.value,
    }
  }

  const _ComputeFooterProps = (): ComponentProps<typeof Footer> => {
    if (form.state === 'new')
      return {
        useSave: {},
      }

    return { useSave: {}, useDelete: {}, locked: p.locked?.value }
  }

  return (
    <OffsetedContainer>
      <FormContext.Provider
        value={{
          submit: formik.submitForm,
          delete: delete_,
          touch: touch,
        }}
      >
        <Form noValidate onSubmit={formik.handleSubmit}>
          {/* ----------- Hidden Inputs Section ----------- */}
          {_Match}
          {/* --------------------------------------------- */}

          <Header {..._ComputeHeaderProps()} />

          <Form.Row>
            <Col as={ItemWrapper}>{_GameMap}</Col>
          </Form.Row>
          <Form.Row>
            <Col as={ItemWrapper}>{_Team}</Col>
          </Form.Row>

          <ItemWrapper />

          <Form.Row>
            <Col as={ItemWrapper}>{_ActionIdent}</Col>
          </Form.Row>

          <Footer {..._ComputeFooterProps()} />
        </Form>
      </FormContext.Provider>
    </OffsetedContainer>
  )
}

export default MatchMapVetoForm
